/* eslint-disable no-param-reassign */
import * as logger from 'loglevel';
import { initTheme } from '@front10/helpers/dist/ThemeBuilder';
import validateCmpConfig from '../../validateCmpConfig';
import { annotateAndLogError, getSanitizedError } from '../../../../helpers/error';
import { getTranslations, translate } from '../../../../_integrations/everymundo/i18n';
import { getValue } from '../../../../helpers/utils';
import { evaluateObject } from '../../../../helpers/evaluators';
import { isSSR } from '@front10/utils';
import { getCoolContext, getPrepopulation } from '../../contextHelper';
import deepmerge from '../../deepmerge';

/**
 * Extract main component metadata to pass it down to the container
 * @param {*} cmpConfig
 */
const getComponentMetaData = (cmpConfig) => {
  return {
    name: cmpConfig.name,
    moduleId: cmpConfig.moduleId,
    version: cmpConfig.version,
    staticAssetsHost: cmpConfig.staticAssetsHost,
    tag: cmpConfig.tag
  };
};

const getMomentFromEM = () => {
  if (!isSSR()) {
    const ingloriousMomentNeverUseMe = getValue(window, 'EM.___fcRuntime.vendors.moment');
    if (ingloriousMomentNeverUseMe) {
      return ingloriousMomentNeverUseMe;
    }
  }
  return () => {};
};

const getLocalizationData = (cmpConfig) => {
  let currentLocale = 'en';
  if (cmpConfig && cmpConfig.pageContext && cmpConfig.pageContext.language) {
    currentLocale = cmpConfig.pageContext.language;
  }
  // For support testing local
  if (cmpConfig.localizationData) {
    return cmpConfig.localizationData;
  }
  if (!isSSR()) {
    const ingloriousMomentNeverUseMe = getValue(window, 'EM.___fcRuntime.vendors.moment');
    const localizationData = getValue(window, 'EM.___fcRuntime.vendors.momentLocales');
    if (ingloriousMomentNeverUseMe) {
      if (localizationData && localizationData[currentLocale]) {
        ingloriousMomentNeverUseMe.locale(currentLocale, localizationData[currentLocale]);
      }
      return ingloriousMomentNeverUseMe.localeData(currentLocale) || false;
    }
    return false;
  }
  return false;
};

function resolveInitialItemsQuery(cmpConfig) {
  const {
    containerConfig: {
      settings: {
        paginationSettings,
        prePopulation,
        faresTrasformationLogic,
        faresTransformationLogic
      }
    }
  } = cmpConfig;

  const initialVisibleItems = paginationSettings
    ? paginationSettings.intialVisibleItems || paginationSettings.initialVisibleItems
    : null;
  const trasformationLogic = faresTrasformationLogic || faresTransformationLogic;
  // TODO: Extract this query logic so it can be shared between server and client
  if (paginationSettings) {
    if (trasformationLogic === 'agreggation') {
      prePopulation.faresLimit = initialVisibleItems;
    } else {
      // defaults to grouped-routes
      prePopulation.routesLimit = paginationSettings.intialVisibleItems;
    }
  }
}

const withCommonPreloads = (
  preloadCustomPropsExecute,
  cmpConfig,
  initialFaresPropertyName,
  getFares
) => {
  return new Promise((resolve, reject) => {
    // Same in all preloads
    const cmpConfigValidation = validateCmpConfig(cmpConfig);
    if (!cmpConfigValidation.isValid) {
      logger.error(cmpConfigValidation.message, cmpConfig);
      reject(cmpConfigValidation.message);
    }

    resolveInitialItemsQuery(cmpConfig);

    const pageContext = getCoolContext(cmpConfig.context);
    cmpConfig.containerConfig.settings.pageContext = pageContext;
    cmpConfig.pageContext = pageContext;
    cmpConfig.containerConfig.settings.prePopulation = getPrepopulation(cmpConfig);
    cmpConfig.containerConfig.settings.meta = getComponentMetaData(cmpConfig);

    const prePopulation = Object.assign({}, cmpConfig.containerConfig.settings.prePopulation);
    // patch to solve EM issue with the evaluation of endpoints
    if (prePopulation.airlineIataCode) {
      prePopulation.airlineCode = prePopulation.airlineIataCode;
    }

    // INFO: This behavior is for the case of airlines that are a conglomerate,
    //       the deeplink post goes with the airlineIataCode of the fare and not
    //       with the prePopulation.
    const skipEvaluatorData = {};
    if (
      cmpConfig.containerConfig.settings.integrationSettings.skipEvaluator &&
      Array.isArray(cmpConfig.containerConfig.settings.integrationSettings.skipEvaluator)
    ) {
      cmpConfig.containerConfig.settings.integrationSettings.skipEvaluator.forEach((item) => {
        skipEvaluatorData[item] = cmpConfig.containerConfig.settings.integrationSettings[item];
      });
    }
    cmpConfig.containerConfig.settings.integrationSettings = evaluateObject(
      cmpConfig.containerConfig.settings.integrationSettings,
      prePopulation
    );

    //INFO: Restoring the same object without evaluating
    cmpConfig.containerConfig.settings.integrationSettings = deepmerge(
      cmpConfig.containerConfig.settings.integrationSettings,
      skipEvaluatorData
    );
    cmpConfig.containerConfig.settings.translate = translate;

    const {
      containerConfig: {
        settings: { integrationSettings, direction }
      }
    } = cmpConfig;

    const { airlineIataCode, language, textDirection } = pageContext;

    // getting the labels
    const labels = getValue(cmpConfig, 'servicesConfig.labels.labels');
    // to be used for RTL evaluation
    if (direction && direction === '@@pageContext.textDirection@@' && textDirection) {
      cmpConfig.containerConfig.settings.direction = textDirection;
    }

    if (!isSSR() && (textDirection === 'RTL' || textDirection === 'rtl')) {
      document.dir = 'rtl';
      cmpConfig.containerConfig.settings.direction = 'RTL';
      const cssLinks = document.querySelectorAll('link[rel="stylesheet"]');
      for (let i = 0; i < cssLinks.length; i++) {
        const cssLink = cssLinks[i];
        const cssUrl = cssLink.href;
        if (cssUrl.includes('em-cmp-fn-styles')) {
          cssLink.href = cssUrl.replace(/em-cmp-fn-styles/g, 'em-cmp-fn-styles-rtl');
        }
      }
    }

    // merge custom headers to integrationSettings headers
    integrationSettings.headers = Object.assign(
      {},
      cmpConfig.customHeaders || {},
      integrationSettings.headers || {}
    );

    // add preloadData from cmpConfig to integrationSettings
    integrationSettings.preloadData = cmpConfig.preloadData;

    const clientPromises = [
      getTranslations(airlineIataCode, language, integrationSettings, labels),
      false
    ];

    const ssrPromises = [getTranslations(airlineIataCode, language, integrationSettings, labels)];

    preloadCustomPropsExecute(
      cmpConfig,
      clientPromises,
      ssrPromises,
      initialFaresPropertyName,
      getFares
    )
      .then((resultCmpConfig) => {
        resultCmpConfig.containerConfig.settings.localizationData = getLocalizationData(
          resultCmpConfig
        );
        resultCmpConfig.containerConfig.settings.moment = getMomentFromEM();
        const themeConfig = { ...resultCmpConfig.containerConfig.theme };
        delete resultCmpConfig.containerConfig.theme;
        if (resultCmpConfig.containerConfig.iconProvider) {
          resultCmpConfig.containerConfig.settings.iconProvider =
            resultCmpConfig.containerConfig.iconProvider;
          delete resultCmpConfig.containerConfig.iconProvider;
        }
        const theme = initTheme(themeConfig);
        resultCmpConfig.containerConfig.settings.theme = theme;
        resolve(resultCmpConfig);
      })
      .catch((err) => {
        if (!cmpConfig) {
          cmpConfig = {};
        }
        if (!cmpConfig.containerConfig) {
          cmpConfig.containerConfig = {};
        }
        const ERROR = annotateAndLogError(err, `PRELOAD_CUSTOM_PROPS`, cmpConfig);
        cmpConfig.containerConfig.criticalError = getSanitizedError(ERROR);
        resolve(cmpConfig);
      });
  });
};

const coolOne = () => {
  return 1;
};

export { withCommonPreloads, coolOne };
